import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import io from 'socket.io-client';
import { getToken } from '../helpers/utils';

const SocketContext = createContext(null);

export const SocketProvider = ({ children }) => {
  const [socket, setSocket] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [reconnectAttempts, setReconnectAttempts] = useState(0);
  const reconnectTimeoutRef = useRef(null);
  const userIdentity = useSelector((state) => state.auth.user?.id ?? state.auth.user?.attributes?.stationChatId ?? null);

  const connectSocket = () => {
    // Clear any existing reconnect timeout
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    const newSocket = io(process.env.REACT_APP_SOCKET_URL, {
      query: { token: getToken() },
      transports: ['websocket', 'polling'],
      reconnectionAttempts: 5,
      timeout: 10000,
      reconnectionDelay: 2000,
      autoConnect: true,
    });
    
    newSocket.$token = getToken();
    setSocket(newSocket);
    setReconnectAttempts(0);

    newSocket.on('connect', () => {
      setIsConnected(true);
      setReconnectAttempts(0);
      console.log('✅ Socket Connected');
    });

    newSocket.on('disconnect', (reason) => {
      setIsConnected(false);
      console.log('🔴 Socket Disconnected:', reason);
      
      // If disconnected unexpectedly, attempt to reconnect
      if (reason === 'io server disconnect' || reason === 'io client disconnect') {
        attemptReconnect();
      }
    });

    newSocket.on('connect_error', (error) => {
      setIsConnected(false);
      console.error('❌ Socket Connection Error:', error);
      attemptReconnect();
    });

    newSocket.on('reconnect_attempt', (attempt) => {
      console.log(`🔄 Reconnection attempt ${attempt}`);
      setReconnectAttempts(attempt);
    });

    newSocket.on('reconnect_failed', () => {
      console.error('❌ All reconnection attempts failed');
      // You could trigger a manual reconnect button here
    });

    newSocket.on("getUsersInRoom", (roomId, callback) => {
      const room = newSocket.sockets.adapter.rooms[roomId];
      if (!room) {
        return callback({ error: "Room not found", users: [] });
      }
      const users = Object.keys(room.sockets || {});
      callback({ users });
    });

    return newSocket;
  };

  const attemptReconnect = () => {
    if (reconnectTimeoutRef.current) return; // Already attempting to reconnect

    const delay = Math.min(3000 * (reconnectAttempts + 1), 15000); // Exponential backoff with max 15s
    
    reconnectTimeoutRef.current = setTimeout(() => {
      console.log(`🔄 Manual reconnection attempt (${reconnectAttempts + 1})`);
      if (socket) {
        socket.disconnect(); // Clean up old socket
      }
      connectSocket(); // Create new connection
      reconnectTimeoutRef.current = null;
    }, delay);
  };

  const manualReconnect = () => {
    console.log('🔄 Manual reconnect triggered');
    attemptReconnect();
  };

  useEffect(() => {
    const newSocket = connectSocket();

    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (newSocket) {
        newSocket.disconnect();
      }
    };
  }, []);

  // When user/station changes (e.g. after "Switch station"), disconnect old socket and reconnect with new token so chat/polls/contest use new station
  useEffect(() => {
    if (userIdentity === undefined || userIdentity === null) return;
    if (!socket) return;

    const currentToken = getToken();
    if (socket.$token !== currentToken) {
      socket.disconnect();
      connectSocket();
    }
  }, [userIdentity]);

  return (
    <SocketContext.Provider value={{ 
      socket, 
      isConnected, 
      reconnectAttempts,
      manualReconnect 
    }}>
      {children}
    </SocketContext.Provider>
  );
};

export const useSocket = () => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
};